/**
 * Types for the Review (Contract Review Execution) feature.
 *
 * Backend API: /reviews/*
 * Models: Review, ReviewDocument, ReviewFinding
 *
 * Re-exports Playbook types for backward compatibility with existing components.
 */

// The local bindings `ReviewState` needs. Must sit above the re-exports
// below: `import/first` is a build error under CRA, and this line previously
// sat next to `ReviewState` in the middle of the file.
import type { PlaybookListItem, PlaybookDetail, ExtractionProgress as ExtProgress } from './playbook';

// ─── Re-export playbook types (backward compat for existing components) ──────

export type {
  ClauseRule,
  GlobalRule,
  FindingSeverity,
  PlaybookSharedUser as ReviewSharedUser,
  PlaybookStatus as ReviewStatus,
  PlaybookListItem as ReviewListItem,
  PlaybookDetail as ReviewDetail,
  ReviewSummary as ReviewSessionSummary,
  PlaybookListResponse as ReviewListResponse,
  CreatePlaybookPayload as CreateReviewPayload,
  UpdatePlaybookPayload as UpdateReviewPayload,
  CreateClauseRulePayload,
  UpdateClauseRulePayload,
  CreateGlobalRulePayload,
  UpdateGlobalRulePayload,
  ExtractionStep,
  ExtractionProgress,
} from './playbook';

// ─── Review Status ───────────────────────────────────────────────────────────

export type ReviewSessionStatus = 'pending' | 'processing' | 'completed' | 'completed_with_errors' | 'failed' | 'pending_sign_off' | 'finalized';

// ─── Review Document ─────────────────────────────────────────────────────────

export interface ReviewDocument {
  id: number;
  file_name: string;
  file_type: string | null;
  file_size: number | null;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  page_count: number | null;
  processed_at: string | null;
}

// ─── Finding ─────────────────────────────────────────────────────────────────

export type FindingDecision = 'accepted' | 'rejected' | 'modified' | 'escalated' | null;

/**
 * Is this decision an ANSWER, or is the finding still open?
 *
 * Escalating means "someone else should decide this" — the finding has been
 * routed, not resolved. It nonetheless lives in the same `decision` field as
 * accepted/rejected/modified, so every truthy check on that field silently
 * counted it as done: the progress bar filled, and Complete Review became
 * available with escalated items still outstanding.
 *
 * This is THE predicate for "resolved" so the reducer, the progress display
 * and the completion dialog cannot drift from each other again. The document
 * viewer already had this rule (`!f.decision || f.decision === 'escalated'`)
 * and was the only place that was right.
 *
 * NOT for "has any decision at all" — showing the decision badge, offering
 * Undo, and the escalated branch of the action bar all legitimately mean the
 * field is set, whatever it says. Those stay as they are.
 */
export const isResolvedDecision = (decision: FindingDecision | undefined): boolean =>
  !!decision && decision !== 'escalated';

export interface FindingNote {
  uuid: string;
  content: string | null;
  author_id: number;
  author_name: string;
  is_mine: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export interface Finding {
  uuid: string;
  document_id: number | null;
  severity: 'critical' | 'major' | 'minor' | 'info';
  clause_type: string;
  action_type: 'replace' | 'add_after' | 'add_before' | null;
  section_reference: string | null;
  original_text: string | null;
  issue_description: string | null;
  suggested_redline: string | null;
  legal_basis: any;
  risk_score: number | null;
  confidence_score: number | null;
  original_text_start_offset: number | null;
  original_text_end_offset: number | null;
  decision: FindingDecision;
  decision_by_name: string | null;
  decision_notes: string | null;
  decision_at: string | null;
  modified_text: string | null;
  applied_at: string | null;
  document_name: string | null;
  notes: FindingNote[];
}

// ─── Collaborator ────────────────────────────────────────────────────────────

export type CollaboratorAccess = 'view' | 'edit' | 'admin';

export interface ReviewCollaborator {
  user_id: string;
  name: string;
  email: string;
  initials: string;
  access: CollaboratorAccess;
  role?: string | null;
  avatar_url?: string | null;
  shared_at?: string | null;
  shared_by?: string | null;
}

// ─── Decision Progress ───────────────────────────────────────────────────────

export interface DecisionProgress {
  total: number;
  decided: number;
  pending: number;
  /**
   * Escalated findings — unresolved, and counted separately from `pending`.
   * Optional while the backend is being changed to send it; derive it from
   * the findings when absent (see `decisionBuckets`).
   */
  escalated?: number;
}

// ─── Decision Summary (from findings list) ───────────────────────────────────

export interface DecisionSummary {
  total: number;
  decided: number;
  pending: number;
  /** See `DecisionProgress.escalated`. */
  escalated?: number;
}

// ─── Audit Log Entry ─────────────────────────────────────────────────────────

export interface AuditLogEntry {
  id: number;
  action: string;
  entity_type: string | null;
  entity_uuid: string | null;
  user_name: string | null;
  details: Record<string, any> | null;
  created_at: string | null;
}

export interface AuditLogResponse {
  items: AuditLogEntry[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// ─── Sign-Off ────────────────────────────────────────────────────────────────

export interface ReviewSignOff {
  user_uuid: string;
  user_name: string;
  role: string;
  signed_off: boolean;
  signed_off_at: string | null;
  notes: string | null;
}

export interface SignOffStatus {
  session_uuid: string;
  status: ReviewSessionStatus;
  finalized_at: string | null;
  signers: ReviewSignOff[];
  all_signed_off: boolean;
  signed_count: number;
  required_count: number;
}

// ─── Document Content (viewer) ───────────────────────────────────────────────

export interface FindingOverlay {
  uuid: string;
  severity: string;
  clause_type: string;
  action_type: 'replace' | 'add_after' | 'add_before' | null;
  section_reference: string | null;
  target_subsection: string | null;
  start_offset: number | null;
  end_offset: number | null;
  original_text: string | null;
  issue_description: string | null;
  suggested_redline: string | null;
  modified_text: string | null;
  decision: FindingDecision;
  decision_by_name: string | null;
  decision_at: string | null;
}

export interface DocumentContent {
  document_id: number;
  file_name: string;
  file_type: string;
  file_size: number | null;
  page_count: number | null;
  url?: string;
  content_base64?: string;
  findings_overlay: FindingOverlay[];
}

// ─── PubSub Config ───────────────────────────────────────────────────────────

export interface PubSubConfig {
  group: string;
  endpoint: string;
  token?: string;
  url?: string;
}

// ─── Review Detail (full, when viewing a review) ─────────────────────────────

export interface ReviewSessionDetail {
  uuid: string;
  name: string | null;
  playbook_uuid: string | null;
  playbook_name: string | null;
  status: ReviewSessionStatus;
  document_count: number;
  jurisdiction: string[];
  overall_risk_score: number | null;
  findings_count: number;
  critical_count: number;
  major_count: number;
  minor_count: number;
  info_count: number;
  documents: ReviewDocument[];
  collaborators: ReviewCollaborator[];
  parent_review_uuid: string | null;
  decision_progress: DecisionProgress;
  finalized_at: string | null;
  pubsub?: PubSubConfig;
  created_at: string | null;
  completed_at: string | null;
  source_table_uuid?: string | null;
  source_row_uuid?: string | null;
}

// ─── Review List Item (for reviews list page) ────────────────────────────────

export interface ReviewSessionListItem {
  uuid: string;
  name: string | null;
  playbook_uuid: string;
  playbook_name: string;
  status: ReviewSessionStatus;
  document_count: number;
  findings_count: number;
  critical_count: number;
  major_count: number;
  minor_count: number;
  info_count: number;
  overall_risk_score: number | null;
  has_access: ReviewCollaborator[];
  deleted_at: string | null;
  created_at: string | null;
  completed_at: string | null;
}

// ─── API Response Wrappers ───────────────────────────────────────────────────

export interface ReviewSessionListResponse {
  items: ReviewSessionListItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface FindingsListResponse {
  findings: Finding[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  decision_summary: DecisionSummary;
}

// ─── Payloads ────────────────────────────────────────────────────────────────

export interface StartReviewPayload {
  files: File[];
  name?: string;
  jurisdiction?: string[];
  matter_uuid?: string;
  data_source_ids?: string[];
  language?: string;
  collaborators?: Array<{
    collaborator_uuid?: string;
    user_id?: string | number;
    access: 'edit' | 'view' | 'admin';
  }>;
}

export interface UpdateFindingDecisionPayload {
  decision: 'accepted' | 'rejected' | 'modified' | 'escalated';
  notes?: string;
  modified_text?: string;
}

export interface AddCollaboratorPayload {
  collaborator_id: string;
  access: CollaboratorAccess;
  name?: string;
  email?: string;
  initials?: string;
  role?: string;
  avatar_url?: string;
}

export interface UpdateCollaboratorPayload {
  access: CollaboratorAccess;
}

export interface SubmitSignOffPayload {
  notes?: string;
}

// ─── Redux State (combined playbook + review for now) ────────────────────────

export interface ReviewState {
  /** Playbook list */
  items: PlaybookListItem[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
  isLoading: boolean;

  /** Reviews list */
  sessionItems: ReviewSessionListItem[];
  sessionTotalItems: number;
  sessionCurrentPage: number;
  sessionTotalPages: number;
  isLoadingSessions: boolean;

  /** Playbook detail */
  currentReview: PlaybookDetail | null;
  isLoadingDetail: boolean;

  /** Review detail */
  currentSession: ReviewSessionDetail | null;
  isLoadingSession: boolean;

  /** Findings */
  findings: Finding[];
  findingsTotal: number;
  findingsPage: number;
  isLoadingFindings: boolean;
  decisionSummary: DecisionSummary | null;

  /** Collaborators (for current session) */
  collaborators: ReviewCollaborator[];
  isLoadingCollaborators: boolean;

  /** Audit log (for current session) */
  auditLog: AuditLogEntry[];
  auditLogTotal: number;
  auditLogPage: number;
  isLoadingAuditLog: boolean;

  /** Document content (for viewer) */
  documentContent: DocumentContent | null;
  isLoadingDocument: boolean;

  /** Sign-off status */
  signOffStatus: SignOffStatus | null;
  isLoadingSignOff: boolean;

  /** Processing */
  isUploading: boolean;
  isExtractingPlaybook: boolean;

  /** Playbook extraction progress */
  extractionProgress: ExtProgress;

  /** Error */
  error: string | null;
}
